import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
  articleTags?: string[];
  schemaData?: object;
  noindex?: boolean;
}

function setMetaTag(selector: string, content: string, attrName = 'content') {
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    const isProperty = selector.startsWith('meta[property');
    const nameMatch = selector.match(/\[(name|property)="([^"]+)"\]/);
    if (nameMatch) {
      el.setAttribute(nameMatch[1], nameMatch[2]);
    }
    document.head.appendChild(el);
  }
  el.setAttribute(attrName, content);
}

function removeMetaTag(selector: string) {
  const el = document.querySelector(selector);
  if (el) el.remove();
}

export default function SEO({
  title,
  description,
  keywords,
  canonicalUrl = 'https://jingpinyiquercu.com/',
  ogType = 'website',
  ogImage,
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
  articleTags,
  schemaData,
  noindex = false,
}: SEOProps) {
  useEffect(() => {
    document.title = title;
    setMetaTag('meta[name="description"]', description);
    if (keywords) setMetaTag('meta[name="keywords"]', keywords);
    else removeMetaTag('meta[name="keywords"]');

    setMetaTag('meta[name="robots"]', noindex ? 'noindex, follow' : 'index, follow');

    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', canonicalUrl);

    setMetaTag('meta[property="og:type"]', ogType);
    setMetaTag('meta[property="og:title"]', title);
    setMetaTag('meta[property="og:description"]', description);
    setMetaTag('meta[property="og:url"]', canonicalUrl);
    setMetaTag('meta[property="og:site_name"]', '精品一区二区');
    setMetaTag('meta[property="og:locale"]', 'zh_CN');
    if (ogImage) setMetaTag('meta[property="og:image"]', ogImage);
    else removeMetaTag('meta[property="og:image"]');

    setMetaTag('meta[name="twitter:card"]', 'summary_large_image');
    setMetaTag('meta[name="twitter:title"]', title);
    setMetaTag('meta[name="twitter:description"]', description);
    if (ogImage) setMetaTag('meta[name="twitter:image"]', ogImage);
    else removeMetaTag('meta[name="twitter:image"]');

    if (articlePublishedTime) setMetaTag('meta[property="article:published_time"]', articlePublishedTime);
    else removeMetaTag('meta[property="article:published_time"]');

    if (articleModifiedTime) setMetaTag('meta[property="article:modified_time"]', articleModifiedTime);
    else removeMetaTag('meta[property="article:modified_time"]');

    if (articleAuthor) setMetaTag('meta[property="article:author"]', articleAuthor);
    else removeMetaTag('meta[property="article:author"]');

    document.querySelectorAll('meta[property="article:tag"]').forEach(el => el.remove());
    if (articleTags) {
      articleTags.forEach(tag => {
        const el = document.createElement('meta');
        el.setAttribute('property', 'article:tag');
        el.setAttribute('content', tag);
        document.head.appendChild(el);
      });
    }

    const existingScript = document.querySelector('script[data-schema="true"]');
    if (existingScript) existingScript.remove();
    if (schemaData) {
      const script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-schema', 'true');
      script.textContent = JSON.stringify(schemaData);
      document.head.appendChild(script);
    }
  }, [
    title, description, keywords, canonicalUrl, ogType, ogImage,
    articlePublishedTime, articleModifiedTime, articleAuthor,
    articleTags, schemaData, noindex,
  ]);

  return null;
}